<template>
  <!-- A card that floats over a flat scrim, as the system's dialog does — drawn by hand because this popup is of the
       critical lane (no library component), and it can fire while offline, so nothing here needs the network. -->
  <div v-if="show" class="timeout-popup-overlay" data-testid="timeout-popup">
    <div
      class="timeout-popup"
      role="dialog"
      aria-modal="true"
      aria-labelledby="timeout-popup-title"
    >
      <BzIcon name="error" class="timeout-icon" />
      <h3 id="timeout-popup-title" class="timeout-title">{{ title || $t('message.timeout') }}</h3>
      <p v-if="errorMessage" class="timeout-message" data-testid="timeout-message">{{ errorMessage }}</p>
      <button ref="accept" class="timeout-btn" @click="$emit('close')">{{ $t('message.accept') }}</button>
    </div>
    <audio ref="timeoutSound" src="/timeout.mp3"></audio>
  </div>
</template>

<script>
import Logger from '@/logger';
import BzIcon from '@/ui/components/BzIcon.vue';

const logger = new Logger({screen: 'TimeoutPopupComponent'});

export default {
  name: 'TimeoutPopupComponent',
  components: { BzIcon },
  props: {
    show: {
      type: Boolean,
      required: true
    },
    errorMessage: {
      type: String,
      default: ''
    },
    // Optional title override — the popup is reused for non-analysis errors
    // (e.g. manual order creation), where the default analysis title is wrong.
    title: {
      type: String,
      default: ''
    }
  },
  watch: {
    show(newValue) {
      if (newValue) {
        this.playTimeoutSound();
        // A modal takes the focus in: the keyboard (and a screen reader) start on its one action.
        this.$nextTick(() => this.$refs.accept && this.$refs.accept.focus({ preventScroll: true }));
      }
    }
  },
  methods: {
    playTimeoutSound() {
      const sound = this.$refs.timeoutSound;
      if (sound) {
        sound.currentTime = 0; // Reset the audio to the beginning
        sound.play().catch(error => logger.error('Error playing timeout sound', {error: error}));
      }
    }
  },
  emits: ['close']
}
</script>

<style scoped>
/* The system's dialog card, in the lane's own CSS: the raised surface with its edge, 20 px corners and the short
   shadow, over the flat scrim (no blur: norm 11, and it costs the €100 tablet). */
.timeout-popup-overlay {
  position: fixed;
  inset: 0;
  box-sizing: border-box;
  padding: var(--bz-space-4);
  background-color: var(--bz-scrim);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: var(--bz-z-lane-popup);
}

.timeout-popup {
  box-sizing: border-box;
  width: min(var(--bz-dialog-width), 100%);
  max-height: 100%;
  overflow-y: auto;
  padding: var(--bz-space-6);
  border: 1px solid var(--bz-edge);
  border-radius: var(--bz-radius-xl);
  background: var(--bz-raised);
  color: var(--bz-ink);
  box-shadow: var(--bz-shadow-overlay);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--bz-space-3);
  font-family: var(--bz-font);
  font-optical-sizing: auto;
  font-synthesis: none;
  font-kerning: normal;
}

/* Calm, not alarming: the icon is the second ink — the words say what happened. */
.timeout-icon {
  font-size: var(--bz-icon-lg);
  color: var(--bz-ink-2);
}

/* 22/28 at 600: a dialog's title. */
.timeout-title {
  margin: 0;
  font-family: var(--bz-font-display);
  font-size: var(--bz-text-xl);
  line-height: var(--bz-leading-xl);
  letter-spacing: var(--bz-tracking-xl);
  font-weight: var(--bz-weight-label);
  color: var(--bz-ink);
  overflow-wrap: anywhere;
}

/* 18/24: the body of a dialog. */
.timeout-message {
  margin: 0;
  font-size: var(--bz-text-lg);
  line-height: var(--bz-leading-lg);
  letter-spacing: var(--bz-tracking-lg);
  font-weight: var(--bz-weight-body);
  color: var(--bz-ink-2);
  white-space: pre-line;
  overflow-wrap: anywhere;
}

/* The one action: the primary button, 72 high with the 72's 16 px corners. */
.timeout-btn {
  box-sizing: border-box;
  align-self: stretch;
  min-height: var(--bz-target-primary);
  margin-top: var(--bz-space-3);
  padding: 0 var(--bz-space-4);
  border: 0;
  border-radius: var(--bz-radius-lg);
  background: var(--bz-action);
  color: var(--bz-on-action);
  font-family: inherit;
  font-size: var(--bz-text-lg);
  line-height: var(--bz-leading-lg);
  letter-spacing: var(--bz-tracking-lg);
  font-weight: var(--bz-weight-label);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
  touch-action: manipulation;
}

.timeout-btn:focus-visible {
  outline: 3px solid var(--bz-focus);
  outline-offset: 2px;
}

/* On a phone, in the system's lane, the error takes the whole screen (Adrián, 14-oct-2026): the analysis
   failed and nothing behind it can be used until it is read. The words in the middle of what is left, the
   one action at the bottom, in reach of the thumb, inside the safe areas. */
@media (max-width: 767px) {
  :root[data-bz-lane="system"] .timeout-popup-overlay {
    padding: 0;
    background: var(--bz-bg);
  }

  :root[data-bz-lane="system"] .timeout-popup {
    width: 100%;
    height: 100%;
    max-height: none;
    padding: calc(var(--bz-gutter) + env(safe-area-inset-top, 0px)) var(--bz-gutter) calc(var(--bz-gutter) + env(safe-area-inset-bottom, 0px));
    border: 0;
    border-radius: 0;
    background: var(--bz-bg);
    box-shadow: none;
  }

  :root[data-bz-lane="system"] .timeout-icon {
    margin-top: auto;
    font-size: var(--bz-target-md);
  }

  :root[data-bz-lane="system"] .timeout-btn {
    margin-top: auto;
  }
}
</style>
